import { formatNumber, PercentPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';

import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { DciGridDefaultsStateKey, DciTabState } from '@approot/models';
import { BusinessDay } from '@approot/models/businessDay';
import { DciTab } from '@approot/models/dci-tab.model';
import {
  LegService,
  MarketDataService,
  PricingExtraService,
  ProductsConfigService,
  SpotResponse,
  UserStorageService
} from '@approot/services';
import {
  DateUtilities,
  exponentialDelay,
  reformatDate,
  sleep,
  SubscriptionSink,
  Utilities,
  shortUuid
} from '@approot/utilities';
import { fromEvent, Subscription } from 'rxjs';

type TradeDates = {
  expirationDate: string;
  settlementDate: string;
};

@Component({
  selector: 'app-dci-tab',
  templateUrl: './dci-tab.component.html',
  styleUrls: ['./dci-tab.component.scss']
})
export class DciTabComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() tabData: DciTab;
  @Input() controlPanelForm: FormGroup;
  @Output() loadStrategy = new EventEmitter<any>();
  tabForm: FormGroup;
  pipsPctList = ['pips', 'pct'];
  dayDifference = ['', '', '', '', '', ''];
  cutOffs = [];
  selectedPercentField;
  calculating = false;
  spotManualDate = false;
  windowKeyDownEventListener: Subscription;
  datesInfo = {};
  pricingSubscription: Subscription;
  private streamingWebsocketUUID = 'stream_' + shortUuid();
  private tenors: string[];
  private strikesYields: string[];
  private subsink = new SubscriptionSink();
  constructor(
    private legService: LegService,
    private productsConfigService: ProductsConfigService,
    private pricingExtraService: PricingExtraService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private webSocketService: GlobalWebsocketService,
    private percentPipe: PercentPipe,
    private marketDataService: MarketDataService,
    private userStorageService: UserStorageService
  ) {}

  public ngAfterViewInit(): void {
    let spot = this.elementRef.nativeElement.querySelector('#spot');
    if (spot) {
      spot.addEventListener(
        // if the user types turn text orange
        'input',
        this.manualSpotChange.bind(this)
      );

      spot.addEventListener(
        // if they blur on the spot
        'blur',
        this.decimalFormatterOnBlur.bind(
          this,
          this.tabForm.get('spot'),
          'spot',
          null
        )
      );
    }
  }

  ngOnDestroy(): void {
    this.windowKeyDownEventListener.unsubscribe();
    this.subsink.unsubscribe();
  }

  ngOnInit() {
    this.dciWSListener();
    this.tabForm = new FormGroup({
      cutOff: new FormControl('', Validators.required),
      spot: new FormControl(this.tabData.spot.value || '', Validators.required),
      dates: this.makeDateControls(
        this.tabData.tabStorage.dates || this.defaultDates
      ),
      incrementor: new FormControl(
        this.tabData.tabStorage.incrementor &&
        this.tabData.tabStorage.incrementor !== null
          ? this.tabData.tabStorage.incrementor
          : 1,
        [Validators.pattern('^-?\\d+(.\\d+)?|^$')]
      ),
      pipsPct: new FormControl(this.tabData.tabStorage.pipsPct || 'pct'),
      strikesYields: new FormArray([])
    });

    this.makeStrikes(
      // load the strikes/yields
      this.tabData.tabStorage.strikesYields &&
        this.tabData.tabStorage.strikesYields.length
        ? this.tabData.tabStorage.strikesYields
        : [], // if none is stored use empty array
      this.controlPanelForm.get('solveFor').value
    ); // set all the strikes

    if (
      // if in strike mode and there is no yields load the default as 2.0
      this.controlPanelForm.get('solveFor').value === 'Strike' &&
      !(
        this.tabData.tabStorage.strikesYields &&
        this.tabData.tabStorage.strikesYields.length
      )
    ) {
      (this.tabForm.get('strikesYields') as FormArray).controls[0].setValue(
        '2.0'
      );
      this.strikeChanges(0, this.tabForm.get('pipsPct').value); // recalculate all the strikes based on the set first strike/yield
    }

    if (this.controlPanelForm.get('currency').value) {
      this.setCutOff(); // if the currency has already been set get the cutoff nad latest spot

      if (this.tabData.tabStorage.cutOff) {
        this.tabForm.get('cutOff').setValue(this.tabData.tabStorage.cutOff);
      } else if (this.cutOffs.includes('Tokyo 3pm')) {
        this.tabForm.get('cutOff').setValue('Tokyo 3pm');
      }
      if (!this.tabData.spot.value) {
        this.getLatestSpot(); // if no spot stored load one
      } else if (this.tabData.spot.freeze) {
        this.manualSpotChange(); // mark spot orange
      }
    }

    if (this.controlPanelForm.get('solveFor').value === 'Strike') {
      // strike only has pct option
      this.pipsPctList = ['pct'];
    }

    this.controlPanelForm.get('solveFor').valueChanges.subscribe(val => {
      (this.tabForm.get('strikesYields') as FormArray).controls = [];

      if (val === 'Yield') {
        this.tabForm.get('pipsPct').setValue('pips'); // default for when solve by yield is chosen
        this.pipsPctList = ['pips', 'pct'];
      } else {
        this.tabForm.get('pipsPct').setValue('pct');
        this.pipsPctList = ['pct'];
      }
      this.tabForm.get('incrementor').setValue(1);

      this.tabData.tabStorage.strikesYields = []; // wipe all yields/strikes as they will be reset to their defaults
      this.saveToStorage('strikesYields', []);
      this.makeStrikes([], val);

      (this.tabForm.get('strikesYields') as FormArray).controls[0].setValue(
        // set the first strike/yield
        val === 'Yield' ? this.tabForm.get('spot').value : '2.0'
      );
      this.strikeChanges(0, this.tabForm.get('pipsPct').value); // recalculate all the strikes based on the set first strike/yield
    });

    this.tabForm.get('pipsPct').valueChanges.subscribe(val => {
      this.strikeChanges(0, val); // we want to re calculate strikes when the increment changes
      this.saveToStorage('pipsPct', val);
    });

    this.tabForm.get('incrementor').valueChanges.subscribe(val => {
      if (this.tabForm.get('incrementor').valid) {
        this.strikeChanges(0, this.tabForm.get('pipsPct').value); // we want to re calculate strikes when the increment changes
        this.saveToStorage('incrementor', val); // save to storage
      }
    });

    this.controlPanelForm.get('currency').valueChanges.subscribe(val => {
      this.dates.forEach(date => {
        date.get('expiryDate').setValue('');
      });
      if (val) {
        this.setCutOff();
        this.tabForm.get('cutOff').setValue(this.cutOffs[0]); // preset it to the first option
        this.getLatestSpot();
      } else {
        this.tabForm.get('cutOff').setValue('');
        this.cutOffs = [];
      }
      this.saveToStorage('cutOff', this.tabForm.get('cutOff').value);
    });

    this.controlPanelForm.valueChanges.subscribe(val => {
      // when values change we should cancel the request we have sent
      this.unSubFromPricing();
    });

    this.tabForm.valueChanges.subscribe(val => {
      // when values change we should cancel the request we have sent
      this.unSubFromPricing();
    });

    // this.allowPricing = true;

    this.windowKeyDownEventListener = fromEvent(document, 'keydown').subscribe(
      event => {
        // we want to listen to the f9 button press
        this.keypress(event);
      }
    );
  }

  unSubFromPricing() {
    // when values change we should cancel the request we have sent
    // done by unsubscribing from our http request

    if (this.pricingSubscription) {
      this.pricingSubscription.unsubscribe();
      this.calculating = false; // remove spinner also
    }
  }

  updateDateDifferences() {
    if (this.controlPanelForm.get('startDate').valid) {
      (this.tabForm.get('dates') as FormArray).controls.forEach(
        // when the start date changes calculate the date differences
        (dateControl: FormControl, index: number) => {
          if (
            dateControl.get('settlementDate').valid &&
            dateControl.get('settlementDate').value
          ) {
            this.dayDifference[index] = this.calculateDateDifferences(
              // set the difference in dates
              new Date(dateControl.get('settlementDate').value)
            );
          }
        }
      );
    }
  }

  keypress(event) {
    if (
      event.keyCode === 120 &&
      this.tabForm.valid &&
      this.controlPanelForm.valid &&
      !this.calculating
    ) {
      // if the key is f9 and the forms are valid, price the dci
      this.calculateDci();
    }
  }

  loadSpotToStrike(val: string) {
    // updates the first strike to the yield
    if (
      this.tabForm.get('strikesYields').value[0] === '' &&
      this.controlPanelForm.get('solveFor').value === 'Yield'
    ) {
      (
        (this.tabForm.get('strikesYields') as FormArray)
          .controls[0] as FormControl
      ).setValue(val);
      this.strikeChanges(0, this.tabForm.get('pipsPct').value); // we want to re calculate strikes as the first value changes
    }
  }
  calculateDci() {
    if (this.tabForm.valid && this.controlPanelForm.valid) {
      this.calculating = true;
      this.prepareDciPricing();
    }
  }
  prepareDciPricing() {
    const tenors = this.tabForm.get('dates').value.map(dateGroup => {
      if (DateUtilities.isPrettyDate(dateGroup.expiryDate)) {
        return DateUtilities.prettyDateToDate(dateGroup.expiryDate);
      }
      return dateGroup.expiryDate;
    });
    const task = {
      taskType: 'dcdGrid',
      taskParameters: {
        depositTaker: this.controlPanelForm.get('depositTaker').value,
        depositCurrency: this.controlPanelForm.get('currency').value,
        alternativeCurrency: this.tabData.currency,
        depositDate: DateUtilities.prettyDateToDate(
          this.controlPanelForm.get('startDate').value
        ),
        notional: parseFloat(
          this.controlPanelForm.get('amount').value.replace(/,/g, '')
        ),
        cutOff:
          this.productsConfigService.cutOffTimesAndFixingSources.cutOffs.filter(
            x => x.longName === this.tabForm.get('cutOff').value
          )[0].code,
        dayCountConvention: 'Act/360',
        isInterestConvertible:
          this.controlPanelForm.get('interest').value === 'Converted'
            ? 'Yes'
            : 'No',
        targetMarkUp:
          Utilities.percentageToNumber(
            this.controlPanelForm.get('marginBps').value
          ) || 0,
        tenors: tenors.filter(date => date !== ''),
        spot: this.tabForm.get('spot').value
      }
    };

    if (this.controlPanelForm.get('solveFor').value === 'Strike') {
      // if we have yields convert the percentages into numbers
      // we also filter out empty values
      task.taskParameters['rates'] = this.tabForm
        .get('strikesYields')
        .value.filter(strike => strike !== '')
        .map(yieldValue => Utilities.percentageToNumber(yieldValue));
    } else {
      task.taskParameters['strikes'] = this.tabForm
        .get('strikesYields')
        .value.filter(rate => rate !== '');
    }

    this.tabData.resultSolveFor = this.controlPanelForm.get('solveFor').value;
    this.tabData.results.values = [];
    this.tenors = tenors;
    this.strikesYields = this.tabForm.get('strikesYields').value;
    const strategy = this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      [],
      task
    );
    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}dcdGrid`,
      payload: strategy,
      uuid: this.streamingWebsocketUUID
    });
  }

  public handlePriceDciResponse(
    resp: any,
    tenors: string[],
    strikesYields: string[]
  ) {
    let minValue = null;
    let maxValue = null;
    let continuousValue = 0;
    let numberOfRealValues = 0;
    tenors.forEach((tenor: string, index: number) => {
      const fieldToLookAt =
        this.tabData.resultSolveFor === 'Yield' // depending on if strikes or yields are sent response is different
          ? 'fairRatesForStrikes'
          : 'fairStrikesForRates';
      if (resp.resultByTenor[tenor]) {
        let responseIndexRun = 0; // holds the of the next possible strike in the response
        const newValues = strikesYields.map(strikeYield => {
          // we want to go through the strikes/yields sent, if there is a value sent,
          // look at the response and get the value for the strike/yield else, fill it with an empty array
          // this way we are matching fields up to one another.
          if (strikeYield !== '') {
            // if there is a strike value use at the response index we are at
            responseIndexRun++;
            const responseValue =
              resp.resultByTenor[tenor][fieldToLookAt][responseIndexRun - 1];
            if (responseValue !== 'null') {
              // we want to store the max and min values from the values received
              // since we  are already going through the values to remove nulls and fill blank space
              // we should also get the min and max value
              minValue =
                minValue === null || responseValue < minValue
                  ? responseValue
                  : minValue;

              maxValue =
                maxValue === null || responseValue > maxValue
                  ? responseValue
                  : maxValue;

              continuousValue += +responseValue;
              numberOfRealValues++;
              return responseValue;
            }
          }
          // should be empty as either no strike/yield was sent, or the backend return null for it.
          return '';
        });
        this.tabData.results.values.push({
          events: resp.resultByTenor[tenor].events, // events for the dates on the currencyPair
          values: newValues,
          treasuryRate: resp.treasuryRateByTenor[tenor]
        });
      } else {
        this.tabData.results.values.push({
          events: '',
          treasuryRate: null,
          values: []
        });
      }
    });

    this.tabData.results.minValue = minValue;
    this.tabData.results.maxValue = maxValue;
    this.tabData.results.meanValue = continuousValue / numberOfRealValues;
    this.calculating = false;
  }

  public setCutOff() {
    const cpVals = this.controlPanelForm.value;
    this.productsConfigService
      .getDCICutOffs(cpVals.currency, this.tabData.currency)
      .subscribe({
        next: cutoffs => {
          const allCutoffs =
            this.productsConfigService.cutOffTimesAndFixingSources.cutOffs;
          this.cutOffs = allCutoffs
            .filter(cutoff => cutoffs.includes(cutoff.code))
            .map(cutoff => cutoff.longName);
          this.tabForm.get('cutOff').setValue(this.cutOffs[0]);
        }
      });
  }

  strikeChanges(index: number, pipPctVal) {
    const decimalPlacesFormat =
      this.productsConfigService.decimalPlacesForCrossPipe(this.cross);
    if (
      pipPctVal &&
      index === 0 && // if we change the first strike we will need to change subsequent strikes
      this.tabForm.get('incrementor').value !== '' &&
      (this.tabForm.get('strikesYields') as FormArray).controls.length &&
      (this.tabForm.get('strikesYields') as FormArray).controls[index].value !==
        '' &&
      (this.tabForm.get('strikesYields') as FormArray).controls[index].valid
    ) {
      const solveFor = this.controlPanelForm.get('solveFor').value;
      if (solveFor !== 'Strike' || pipPctVal !== 'pips') {
        let increment =
          this.tabData.currencyPair.substring(3, 6) ===
            this.controlPanelForm.get('currency').value && solveFor === 'Yield'
            ? -this.tabForm.get('incrementor').value
            : +this.tabForm.get('incrementor').value;

        const addOne = solveFor === 'Yield' && pipPctVal === 'pct'; // should 1 be added to the increment

        let firstValue = +(
          this.tabForm.get('strikesYields') as FormArray
        ).controls[0].value.replace('%', ''); // if there are %'s remove them

        let newAddedOrMultiplied;
        if (pipPctVal === 'pct') {
          if (solveFor === 'Strike') {
            newAddedOrMultiplied = increment;
          } else {
            newAddedOrMultiplied = increment / 100;
          }
        } else {
          newAddedOrMultiplied =
            increment / this.tabData.forwardPointsMultiplier;
        }

        const strikeLength = (this.tabForm.get('strikesYields') as FormArray)
          .controls.length; // get the length of the number of strikes to loop through them

        for (let i = 1; i < strikeLength; i++) {
          let valueToSet;

          if (addOne) {
            // determines if we are adding 1 and multiplying or just adding
            valueToSet = firstValue * (1 + i * newAddedOrMultiplied);
          } else {
            valueToSet = firstValue + i * newAddedOrMultiplied;
          }

          valueToSet = formatNumber(
            valueToSet,
            'en-GB',
            solveFor === 'Yield' ? decimalPlacesFormat : '1.1-1' // percentages formatted by 0.1 and strikes 2 or 4 d.p.
          ).replace(/,/g, '');

          (this.tabForm.get('strikesYields') as FormArray).controls[i].setValue(
            valueToSet
          );

          (this.tabForm.get('strikesYields') as FormArray).controls[
            i
          ].markAsTouched(); // we want the textbox to turn red whenever it is false
        }
      }
    } else if (index !== 0) {
      this.tabForm.get('incrementor').setValue(''); // wipe the increment since we are changing a strike that not the middle
    }
    this.saveToStorage(
      'strikesYields',
      this.tabForm.get('strikesYields').value
    );
  }

  get dates() {
    return (this.tabForm.get('dates') as FormArray).controls;
  }

  makeDateControls(dateStorage: any) {
    const datesArray = new FormArray([]);
    for (let i = 0; i < 6; i++) {
      this.datesInfo[i] = {
        expiryDate: { info: '' },
        settlementDate: { info: '' },
        treasuryRate: { info: '' }
      };
      const date = dateStorage[i];
      datesArray.push(this.dateGroupMaker(date, i));
    }
    return datesArray;
  }

  get defaultDates() {
    // default dates
    return {
      0: { expiryDate: '1w' },
      1: { expiryDate: '2w' },
      2: { expiryDate: '1m' },
      3: { expiryDate: '2m' },
      4: { expiryDate: '3m' },
      5: { expiryDate: '' }
    };
  }

  makeStrikes(strikes: string[], solveFor: string) {
    for (let i = 0; i < 8; i++) {
      // makes the strikes uses for loop as this should/was dynamic
      (this.tabForm.get('strikesYields') as FormArray).push(
        this.makeStrikeControl(strikes[i])
      );
    }
  }

  get StrikeYieldsControls() {
    return (this.tabForm.get('strikesYields') as FormArray).controls;
  }

  handleSpotResponseFormat(value) {
    // doNotEmit indicates whether we want the form control to emit a value.
    // this is because emitting a value results in the price being cleared
    // which we don't want to happen in the cases of auto refreshing.
    let decimalPlacesFormat;
    // Hungarian and Japanese currencies only require 2 decimal places, the rest need 4
    decimalPlacesFormat = this.productsConfigService.decimalPlacesForCrossPipe(
      this.cross
    );

    const spotValue = formatNumber(value, 'en-GB', decimalPlacesFormat).replace(
      /,/g,
      ''
    );

    this.tabForm.get('spot').setValue(spotValue);

    this.tabData.spot.value = spotValue;

    this.loadSpotToStrike(spotValue); // load the spot into the first strike

    this.spotManualDate = false;
  }

  manualSpotChange() {
    this.spotManualDate = true;
  }

  decimalFormatterOnBlur(
    formControl: AbstractControl,
    attributeName: string,
    index: number
  ) {
    if (
      formControl.valid ||
      (attributeName === 'spot' && !this.tabForm.get('spot').value)
    ) {
      let formattedValue = formControl.value.replace('%', '');
      if (
        this.cross &&
        formControl.value &&
        (attributeName === 'spot' ||
          this.controlPanelForm.get('solveFor').value === 'Yield')
      ) {
        // format the decimal based on the cross if we are dealing with the spot or a strike value
        formattedValue = this.productsConfigService.crossDecimalFormatter(
          formControl.value,
          this.cross
        );
        formControl.setValue(formattedValue);
      } else if (
        formControl.value &&
        attributeName === 'strikeYield' &&
        this.controlPanelForm.get('solveFor').value === 'Strike'
      ) {
        // decimals should have at least one decimal place
        formattedValue = formatNumber(
          formattedValue,
          'en-GB',
          '1.1-12' // percentages formatted by 0.1 and strikes 2 or 4 d.p.
        ).replace(/,/g, '');
        formControl.setValue(formattedValue);
      }

      if (attributeName === 'spot' && formControl.value === '') {
        // if spot is blured with no value, get latest spot
        this.getLatestSpot();
      } else if (attributeName === 'spot') {
        // if spot has a value freeze it
        this.tabData.spot.value = formControl.value;
        this.tabData.spot.freeze = true;
        this.loadSpotToStrike(formattedValue);
      }

      if (attributeName === 'strikeYield') {
        this.saveToStorage(
          'strikesYields',
          this.tabForm.get('strikesYields').value
        ); // save changes

        if (index === 0) {
          this.strikeChanges(0, this.tabForm.get('pipsPct').value); // we want to re calculate strikes when the increment changes
        }
      }
    }
  }

  get cross(): string {
    return this.tabData.currencyPair;
  }

  makeStrikeControl(strike: any) {
    return new FormControl(
      strike || '',
      this.controlPanelForm.get('solveFor').value === 'Yield'
        ? [Validators.pattern('^(-?\\d+(\\.\\d*)?||(\\.\\d+))?$')]
        : [this.percentValidator.bind(this)]
    );
  }

  dateGroupMaker(date: any, index: number) {
    const dates = new FormGroup({
      expiryDate: new FormControl('', [this.dateValidator.bind(this)]),
      settlementDate: new FormControl('', [this.dateValidator.bind(this)])
    });

    dates.get('expiryDate').valueChanges.subscribe(val => {
      this.dayDifference[index] = ''; // wipe the difference in days
      dates.get('settlementDate').setValue('', { emitEvent: false }); // wipe other side of dates
      DateUtilities.onDateChanged(dates.get('expiryDate'));
      this.saveToStorage('expiryDate', val, index);
      this.saveToStorage('settlementDate', '', index); // wipe the settlement date. On load we will get it from the expiry date

      this.calculateExpirationSettlementDate(
        // calculate the settlement and expiration date
        dates.get('expiryDate'),
        dates.get('settlementDate'),
        'expiryDate',
        index
      );
    });

    dates.get('settlementDate').valueChanges.subscribe(val => {
      dates.get('expiryDate').setValue('', { emitEvent: false }); // wipe other side of dates
      this.dayDifference[index] = ''; // wipe date differences
      DateUtilities.onDateChanged(dates.get('settlementDate'));
      this.saveToStorage('settlementDate', val, index);
      this.saveToStorage('expiryDate', '', index); // wipe the expiry date we will get this from the settlement date

      this.calculateExpirationSettlementDate(
        // calculate the settlement and expiration date
        dates.get('settlementDate'),
        dates.get('expiryDate'),
        'settlementDate',
        index
      );
    });

    if (date) {
      // load the dates
      setTimeout(() => {
        // clarity date picker functions override this
        let expDate;

        if (date.expiryDate) {
          expDate = date.expiryDate; // use the expiry date stored
        } else if (date.settlementDate || date.expiryDate === '') {
          expDate = ''; // if there is a settlement date, the expiry date is going to be pulled from it
        } else {
          expDate = this.defaultDates[index].expiryDate; // use a default date
        }

        const settlementDate = date.settlementDate;

        if (
          (expDate && DateUtilities.isValidLag(expDate)) ||
          DateUtilities.isCorrectDateFormat(expDate)
        ) {
          dates.get('expiryDate').patchValue(expDate);
          this.renderer.setProperty(
            // ensure that the date loads into the textbox
            document.getElementById('expiryDate' + index),
            'value',
            expDate
          );
        }

        if (
          settlementDate &&
          (DateUtilities.isValidLag(settlementDate) ||
            DateUtilities.isCorrectDateFormat(settlementDate))
        ) {
          dates.get('settlementDate').patchValue(settlementDate);
          this.renderer.setProperty(
            // ensure that the date loads into the textbox
            document.getElementById('settlementDate' + index),
            'value',
            settlementDate
          );
        }
      });
    }
    return dates;
  }

  setDateSpinners(index: number, status: boolean) {
    const dates = (this.tabForm.get('dates') as FormArray).controls[index];
    if (status) {
      dates.get('expiryDate').markAsPending();
      dates.get('settlementDate').markAsPending();
    } else {
      dates.get('expiryDate').enable({ emitEvent: false });
      dates.get('settlementDate').enable({ emitEvent: false });
    }
  }

  calculateExpirationSettlementDate(
    formElem: AbstractControl,
    secondDateControl: AbstractControl,
    attributeName: string,
    index: number
  ) {
    this.datesInfo[index].expiryDate.info = '';
    this.datesInfo[index].settlementDate.info = '';
    // setSettlementDate decides whether the settlement date from the future date service should be set or not
    if (formElem.valid && formElem.value) {
      const payload = DateUtilities.dateOrLagToPayload(
        formElem.value.toLowerCase(),
        this.cross,
        this.pricingExtraService.getMarketDataTimeStamp
      );
      if (attributeName === 'expiryDate') {
        this.callCalculateFutureDate(
          formElem,
          secondDateControl,
          payload,
          index
        );
      } else {
        payload['currencyOrCalendar'] = this.cross ? this.cross : '';
        this.settlementDateCaller(payload, formElem, secondDateControl, index); // calc the next businessDay
      }
    }
  }

  settlementDateCaller(
    payload: any,
    control: AbstractControl,
    secondDateControl: AbstractControl,
    index: number
  ) {
    this.setDateSpinners(index, true);
    // sets both the settlement date and expiry date through the calculateExpirationDateFromSettlementDates
    // first get the next business to avoid non buiness days
    this.legService.nextBusinessDay(payload).subscribe({
      next: (resp: BusinessDay) => {
        if (resp.nextBusinessDay) {
          const payload = {
            currencyPair: this.cross,
            settlementDates: resp.nextBusinessDay
          };
          this.legService
            .calculateExpirationDateFromSettlementDates(payload)
            .then(resp => {
              // next work out the expiration date from the settlement date
              resp.subscribe({
                next: (resp: TradeDates[]) => {
                  this.setReturnedDates(
                    resp[0],
                    secondDateControl,
                    control,
                    index
                  );
                },
                complete: () => {
                  this.setDateSpinners(index, false);
                }
              });
            })
            .catch(() => {
              this.setDateSpinners(index, false);
            });
        } else {
          console.log('failed to get the next businessDay');
        }
      },
      error: () => {
        this.setDateSpinners(index, false);
      }
    });
  }

  callCalculateFutureDate(
    formElem: AbstractControl,
    settlementElem: AbstractControl,
    payload,
    index: number
  ) {
    this.setDateSpinners(index, true);
    this.legService.calculateFutureDate(payload).then(
      p => {
        p.subscribe(
          (resp: any) => {
            if (resp.expirationDate || resp.settlementDate) {
              this.setReturnedDates(resp, formElem, settlementElem, index);
            } else {
              this.setDateSpinners(index, false);
              console.error('Unable to retrieve settlement date.');
            }
          },
          err => {
            this.setDateSpinners(index, false);
          }
        );
      },
      err => {
        this.setDateSpinners(index, false);
      }
    );
  }

  setReturnedDates(
    resp: { expirationDate: string; settlementDate: string },
    expiryControl: AbstractControl,
    settlementControl: AbstractControl,
    index
  ) {
    this.setDateSpinners(index, false);
    // from the response set this into the datapickers
    if (resp.expirationDate) {
      if (
        expiryControl.value &&
        DateUtilities.isValidLag(expiryControl.value)
      ) {
        // if the date is a lag add it to the datepicker
        this.datesInfo[index].expiryDate.info = '(' + expiryControl.value + ')';
      }

      expiryControl.patchValue(
        DateUtilities.dateToPrettyDate(resp.expirationDate),
        { emitEvent: false }
      );
    }

    if (resp.settlementDate) {
      // if we should also set the settlement date
      // this.saveToStorage('settlementDate', resp.settlementDate, index);

      if (
        settlementControl.value &&
        DateUtilities.isValidLag(settlementControl.value)
      ) {
        // if the date is a lag add it to the datepicker
        this.datesInfo[index].settlementDate.info =
          '(' + settlementControl.value + ')';
      }

      settlementControl.patchValue(
        DateUtilities.dateToPrettyDate(resp.settlementDate),
        { emitEvent: false }
      );
      settlementControl.setErrors(null);
    }

    this.dayDifference[index] = this.calculateDateDifferences(
      // set the difference in dates
      new Date(settlementControl.value)
    );
  }

  calculateDateDifferences(settlementDate: Date) {
    // calculate the difference in days from the start date to the settlement date.
    // get the difference of millisecond between dates and divide by 1 day in milliseconds
    if (
      this.controlPanelForm.get('startDate').valid &&
      this.controlPanelForm.get('startDate').value &&
      settlementDate.getTime()
    ) {
      const startDate = DateUtilities.isDateOfSlashFormat(
        // if the start date is in slash format restructure the format
        this.controlPanelForm.get('startDate').value
      )
        ? DateUtilities.restructureDate(
            this.controlPanelForm.get('startDate').value
          )
        : this.controlPanelForm.get('startDate').value;

      const oneDay = 86400000; // 24 * 60 * 60 * 1000 === hours*minutes*seconds*milliseconds
      return (
        Math.round(
          Math.abs(
            (new Date(startDate).getTime() - settlementDate.getTime()) / oneDay
          )
        ).toString() + 'd'
      );
    } else {
      return '';
    }
  }

  removeDateLag(elementId) {
    // remove the date lag
    let spanNode = this.elementRef.nativeElement.querySelector(
      'span.' + elementId + '.date-lag'
    ); // this is where the span should be
    if (spanNode) {
      // if the span is already added, remove it as it may need changes to occur to it.
      let parentNode = this.renderer.parentNode(spanNode); // get the parent to the spanNode
      this.renderer.removeChild(parentNode, spanNode);
    }
  }

  textBoxCaretRestrictionHandler(event, action: string) {
    if (this.controlPanelForm.get('solveFor').value === 'Strike') {
      // used to get the index of the item separating editable parts of text and non editable parts
      const indexLocator = Utilities.getIndexOfSeparator(
        event.target.value,
        '%'
      );
      if (action === 'keydown') {
        Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
      } else {
        Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
      }
    }
  }

  get solveFor() {
    return this.controlPanelForm.get('solveFor').value;
  }

  saveToStorage(attribute: string, value: any, index?: number) {
    // saves data to storage
    let dciGridStorage =
      this.userStorageService.getItem<DciTabState>(DciGridDefaultsStateKey) ||
      {}; // load the grid from storage

    // if the data structure doesn't already exist in storage
    if (!dciGridStorage.tabs) {
      dciGridStorage.tabs = {};
    }

    if (!dciGridStorage.tabs[this.tabData.currency]) {
      dciGridStorage.tabs[this.tabData.currency] = {};
    }

    switch (attribute) {
      case 'expiryDate':
      case 'settlementDate':
        let dateToSave = value;

        if (DateUtilities.isDateOfSlashFormat(dateToSave)) {
          dateToSave = DateUtilities.dateToPrettyDate(
            DateUtilities.restructureDate(dateToSave)
          );
        } else if (DateUtilities.isDateOfDashedFormat(dateToSave)) {
          dateToSave = DateUtilities.dateToPrettyDate(dateToSave);
        }

        // we need to make sure that the structure already exists else create it if it doesn't exist
        if (!dciGridStorage.tabs[this.tabData.currency].dates) {
          dciGridStorage.tabs[this.tabData.currency].dates = {};
        }
        if (!dciGridStorage.tabs[this.tabData.currency].dates[index]) {
          dciGridStorage.tabs[this.tabData.currency].dates[index] = {};
        }

        if (!this.tabData.tabStorage.dates) {
          this.tabData.tabStorage.dates = {};
        }

        if (!this.tabData.tabStorage.dates[index]) {
          this.tabData.tabStorage.dates[index] = {};
        }

        this.tabData.tabStorage.dates[index][attribute] = dateToSave;
        delete this.tabData.tabStorage.dates[index].tenor;

        dciGridStorage.tabs[this.tabData.currency]['dates'][index][attribute] =
          dateToSave; // store the date

        break;
      case 'cutOff':
      case 'incrementor':
      case 'pipsPct':
      case 'strikesYields':
        dciGridStorage.tabs[this.tabData.currency][attribute] = value;
        this.tabData.tabStorage[attribute] = value;
        break;
    }

    this.userStorageService.setItem(DciGridDefaultsStateKey, dciGridStorage);
  }

  dateValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value) {
      if (
        DateUtilities.isCorrectDateFormat(control.value) ||
        DateUtilities.isValidLag(control.value)
      ) {
        return null;
      } else {
        return { invalidEntry: { value: control.value } };
      }
    }
    return null;
  }

  percentValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value === '') {
      return null;
    } else if (control.value === '%') {
      control.setValue('', { emitValue: false });
      return null;
    } else if (!isNaN(control.value)) {
      // if it's a number, add a percent sign and set the control's value
      control.setValue(control.value + '%', { emitEvent: false });
      return null;
    }

    if (new RegExp('^(\\d+(\\.\\d*)?||^(\\.\\d+))%$').test(control.value)) {
      if (this.selectedPercentField) {
        // used to get the index of the item separating editable parts of text and non editable parts
        const indexLocator = Utilities.getIndexOfSeparator(
          this.selectedPercentField.value,
          '%'
        );

        if (this.selectedPercentField.selectionEnd >= indexLocator) {
          this.renderer.setProperty(
            this.selectedPercentField,
            'selectionEnd',
            indexLocator
          );
        }
      }
      return null;
    }

    return { invalidEntry: { value: control.value } };
  }

  public openStrategy(row: number, col: number, val: number) {
    const vals = this.tabForm.value;
    const cpVals = this.controlPanelForm.value;

    const redemptionDate = reformatDate(
      vals.dates[row].settlementDate,
      'YYYY-MM-DD'
    );
    const expiryDate = reformatDate(vals.dates[row].expiryDate, 'YYYY-MM-DD');
    const treasuryRate =
      this.tabData.results.values[row]?.treasuryRate?.toFixed(6);

    if (treasuryRate == null) {
      console.log('No treasury rate found', this.tabData.results.values[row]);
    }

    let conversionRate: any;
    let depositRate = '';
    if (cpVals.solveFor === 'Strike') {
      conversionRate = val.toFixed(4);
    } else {
      depositRate = this.percentPipe.transform(val, '1.2-2');
    }

    const p = {
      redemptionDate,
      notional: parseFloat(cpVals.amount.replace(/,/g, '')),
      depositTaker: cpVals.depositTaker,
      alternativeCurrency: this.tabData.currency,
      treasuryRate,
      conversionRate,
      expiryDate,
      marginType: '% p.a.',
      position: 'Enter',
      cutOff: vals.cutoff,
      depositRate,
      depositCurrency: cpVals.currency,
      productType: 'dual-currencydeposit',
      startDate: reformatDate(cpVals.startDate, 'YYYY-MM-DD'),
      margin: cpVals.marginBps,
      shortName: 'newdcd'
    };

    this.loadStrategy.emit({ products: [p] });
  }

  private getLatestSpot(): void {
    this.tabData.spot.value = '';
    this.tabData.spot.freeze = false;
    if (this.controlPanelForm.get('currency').value) {
      const spotCtrl = this.tabForm.get('spot');
      spotCtrl.markAsPending();
      this.marketDataService
        .calculateSpot({
          cross: this.cross,
          timestamp: this.pricingExtraService.getMarketDataTimeStamp
        })
        .subscribe({
          next: (resp: SpotResponse) => {
            spotCtrl.enable();
            this.handleSpotResponseFormat(resp.spot.mid);
          },
          error: _ => {
            spotCtrl.enable();
          }
        });
    }
  }

  private dciWSListener(): void {
    const resubscribe = (iteration: number) => {
      this.subsink.sink = this.webSocketService
        .getListener({ uuid: this.streamingWebsocketUUID })
        .subscribe({
          next: r => {
            if (r.body) {
              this.handlePriceDciResponse(
                r.body,
                this.tenors,
                this.strikesYields
              );
            }
          },
          error: async err => {
            console.error(err);
            this.calculating = false;
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }
}
